/**
 * soundManifest.ts — maps every game sound to its generated audio file.
 *
 * Every clip was generated via Higgsfield (Mirelo Text to Audio for foley/impacts,
 * Sonilo Music for tonal chimes/fanfares) to a shared warm marimba/bell palette —
 * see docs/SOUND_DESIGN.md. Files live in public/audio/sfx and are served as-is by
 * Vite; no bundling or synthesis step is needed.
 */

const SFX_DIR = '/audio/sfx';

export const SOUND_FILES = {
  // Battle — result jingles
  win: `${SFX_DIR}/win.m4a`,
  lose: `${SFX_DIR}/lose.m4a`,

  // Battle — in-fight sounds
  countdownTick: `${SFX_DIR}/countdown-tick.mp3`,
  countdownGo: `${SFX_DIR}/countdown-go.m4a`,
  attackHit: `${SFX_DIR}/attack-hit.mp3`,
  playerHit: `${SFX_DIR}/player-hit.mp3`,
  heal: `${SFX_DIR}/heal.m4a`,

  // Care — pet action sounds
  feedMeat: `${SFX_DIR}/feed-meat.mp3`,
  feedSnack: `${SFX_DIR}/feed-snack.mp3`,
  clean: `${SFX_DIR}/clean.mp3`,
  partialClean: `${SFX_DIR}/partial-clean.mp3`,
  medicine: `${SFX_DIR}/medicine.m4a`,
  rest: `${SFX_DIR}/rest.m4a`,
  wakeUp: `${SFX_DIR}/wake-up.m4a`,
  refill: `${SFX_DIR}/refill.m4a`,

  // Care — training wheel sounds
  wheelSpin: `${SFX_DIR}/wheel-spin.mp3`,
  wheelResultXP: `${SFX_DIR}/wheel-result-xp.m4a`,
  wheelResultPrize: `${SFX_DIR}/wheel-result-prize.m4a`,
  wheelResultSick: `${SFX_DIR}/wheel-result-sick.m4a`,
  wheelResultSuika: `${SFX_DIR}/wheel-result-suika.m4a`,
  wheelResultNothing: `${SFX_DIR}/wheel-result-nothing.m4a`,

  // Milestones — evolution, level up, adoption, eggs
  evolve: `${SFX_DIR}/evolve.m4a`,
  levelUp: `${SFX_DIR}/level-up.m4a`,
  adoptSuccess: `${SFX_DIR}/adopt-success.m4a`,
  eggHatch: `${SFX_DIR}/egg-hatch.mp3`,
  eggCreated: `${SFX_DIR}/egg-created.m4a`,

  // Notifications — generic toast + PVP matchmaking
  toastSuccess: `${SFX_DIR}/toast-success.m4a`,
  toastError: `${SFX_DIR}/toast-error.mp3`,
  matchFound: `${SFX_DIR}/match-found.m4a`,
} satisfies Record<string, string>;

export type SoundName = keyof typeof SOUND_FILES;

/** Looping battle theme, played behind PVP fights. */
export const BATTLE_MUSIC_FILE = `${SFX_DIR}/battle-music.m4a`;

/** Looping themes under the campaign: the opening story, and one per Tale family (world map). */
export const THEME_MUSIC = {
  intro: `${SFX_DIR}/intro-music.m4a`,
  forest: `${SFX_DIR}/theme-forest.m4a`,
  bay: `${SFX_DIR}/theme-bay.m4a`,
  ember: `${SFX_DIR}/theme-ember.m4a`,
  storm: `${SFX_DIR}/theme-storm.m4a`,
  mirror: `${SFX_DIR}/theme-mirror.m4a`,
  champion: `${SFX_DIR}/theme-champion.m4a`,
  home: `${SFX_DIR}/theme-home.m4a`,
  arena: `${SFX_DIR}/theme-arena.m4a`,
  shop: `${SFX_DIR}/theme-shop.m4a`,
} as const;

export type ThemeName = keyof typeof THEME_MUSIC;

/** Which theme plays on each Tale's map. Two worlds may share a mood. */
export const WORLD_THEME: Record<string, ThemeName> = {
  w1: 'forest',
  w2: 'bay',
  w3: 'ember',
  w4: 'storm',
  w5: 'mirror',
  w6: 'champion',
  w7: 'ember',
  w8: 'storm',
  w9: 'mirror',
  w10: 'champion',
};

/**
 * Which theme plays where — the ONE table of "where does the game have background music".
 *
 * The rule: music belongs on the surfaces a player PLAYS or LINGERS on (caring for a pet, picking
 * a fight, shopping, claiming rewards, the campaign), never on a page someone arrives on from a
 * search or reads (home landing, rankings, profiles, species pages, news, guides, legal).
 * First match wins; a path nothing matches is silent. `'self'` means the page runs its own music
 * (the campaign picks a theme per Tale; battles have the battle loop; Admin is silent except for
 * its story preview, which plays each comic's campaign theme).
 */
export const ROUTE_THEME_RULES: { test: (path: string) => boolean; theme: ThemeName | 'self' }[] = [
  { test: (p) => p === '/campaign', theme: 'self' },
  { test: (p) => p === '/battle', theme: 'self' },
  { test: (p) => p === '/admin', theme: 'self' },
  { test: (p) => p === '/play' || p === '/play-free', theme: 'home' },
  { test: (p) => p === '/adopt', theme: 'home' },
  { test: (p) => /^\/suikamon\/[^/]+\/cards$/.test(p), theme: 'home' },
  { test: (p) => ['/pvp-trial', '/pvp-ranked', '/pvp-squad-ranked'].includes(p), theme: 'arena' },
  { test: (p) => p === '/store' || p === '/daily-rewards', theme: 'shop' },
];

/** The theme for a pathname: a theme, `'self'` (the page owns its music), or null (silence). */
export function themeForPath(path: string): ThemeName | 'self' | null {
  return ROUTE_THEME_RULES.find((r) => r.test(path))?.theme ?? null;
}

/** Plain-language "where it plays" for the themes that are not a Tale's (shown in Admin → Story). */
export const THEME_USED_BY: Partial<Record<ThemeName, string>> = {
  intro: 'the campaign opening',
  home: 'campaign overview · Play · Adopt · pet Skills',
  arena: 'Trial · Ranked · Squad Ranked lobbies',
  shop: 'Store · Daily Rewards',
};
